import { useQuery } from "@tanstack/react-query"

import { getAccessToken, getAuthHeaders } from "@/lib/auth"

/**
 * Polls /api/system/vault-status/ and returns:
 *   - true    → Vault available (or unknown — fail-OPEN)
 *   - false   → Vault confirmed unavailable
 *
 * The endpoint is authenticated. We deliberately do NOT use apiFetch() here
 * because its 401 refresh-and-redirect behavior would yank an unauthenticated
 * visitor to /login on every poll. Instead we gate on token presence and
 * treat any non-success response as "unknown → fail-open" so the outage
 * banner only renders when the server explicitly reports available=false.
 */
async function fetchVaultStatus(): Promise<boolean> {
  const res = await fetch("/api/system/vault-status/", {
    headers: getAuthHeaders(),
  })
  if (!res.ok) return true // unknown — don't scare the user
  const body = await res.json()
  return Boolean(body.available)
}

export function useVaultStatus() {
  // useQuery rules-of-hooks: call unconditionally, gate via `enabled`.
  // typeof window check keeps this SSR-safe (getAccessToken reads localStorage).
  const hasToken = typeof window !== "undefined" && !!getAccessToken()
  return useQuery({
    queryKey: ["vault-status"],
    queryFn: fetchVaultStatus,
    enabled: hasToken,
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  })
}
